import React from 'react'
import type { VariantProps } from 'class-variance-authority'
import clsx from 'clsx'
import { match } from 'ts-pattern'
import { transform } from 'lodash-es'

import { tcva } from 'utils/tcva'
import { Spinner } from 'ui/Spinner/Spinner'

export const FILL_COLORS_CLASSES = {
  dark: '',
  light: '',
  primary: '',
  danger: '',
  gray: '',
  info: '',
  purple: '',
  success: '',
  warning: '',
}

export const SIZE_CLASSES = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-4 py-1 h-8 text-sm',
  md: 'px-6 py-2 h-10',
  lg: 'px-8 py-2.5 h-12',
  xl: 'px-10 py-3 h-14',
}

export const buttonVariants = tcva(['text-sm font-light rounded'], {
  variants: {
    color: transform(FILL_COLORS_CLASSES, (acc, _value, key) => {
      acc[key] = ''
    }),
    // color: FILL_COLORS_CLASSES,
    size: SIZE_CLASSES,
    disabled: { [true as any]: 'opacity-50', [false as any]: '' },
    outline: {
      ghost: 'border-opacity-0 hover:bg-gray-100',
      true: 'border enabled:hover:border-opacity-50 shadow-sm',
      false: 'opacity-100 enabled:hover:opacity-80 disabled:opacity-50 shadow-sm',
    },
  },
  compoundVariants: [
    { outline: 'ghost', color: 'dark', className: 'text-gray-700 border-gray-600 ' },
    { outline: 'ghost', color: 'light', className: '  text-gray-700 border-gray-300' },
    { outline: 'ghost', color: 'primary', className: 'text-primary-700 border-primary-600 ' },
    { outline: 'ghost', color: 'danger', className: 'text-red-700 border-red-600 ' },
    { outline: 'ghost', color: 'gray', className: 'text-gray-700 border-gray-600 ' },
    { outline: 'ghost', color: 'info', className: 'text-blue-700 border-blue-600 ' },
    { outline: 'ghost', color: 'purple', className: 'text-purple-700 border-purple-600 ' },
    { outline: 'ghost', color: 'success', className: 'text-green-700 border-green-600 ' },
    { outline: 'ghost', color: 'warning', className: 'text-yellow-700 border-yellow-600 ' },
    { outline: true, color: 'dark', className: 'text-gray-700 border-gray-600 ' },
    { outline: true, color: 'light', className: '  text-gray-700 border-gray-300' },
    { outline: true, color: 'primary', className: 'text-primary-700 border-primary-600 ' },
    { outline: true, color: 'danger', className: 'text-red-700 border-red-600 ' },
    { outline: true, color: 'gray', className: 'text-gray-700 border-gray-600 ' },
    { outline: true, color: 'info', className: 'text-blue-700 border-blue-600 ' },
    { outline: true, color: 'purple', className: 'text-purple-700 border-purple-600 ' },
    { outline: true, color: 'success', className: 'text-green-700 border-green-600 ' },
    { outline: true, color: 'warning', className: 'text-yellow-700 border-yellow-600 ' },
    { outline: false, color: 'dark', className: 'bg-gray-800  text-white' },
    { outline: false, color: 'light', className: 'bg-gray-100  text-gray-800 hover:bg-gray-200 hover:bg-opacity-100' },
    { outline: false, color: 'primary', className: 'bg-primary-900  text-white' },
    { outline: false, color: 'danger', className: 'bg-red-600  text-white' },
    { outline: false, color: 'gray', className: 'bg-gray-600  text-white' },
    { outline: false, color: 'info', className: 'bg-blue-600  text-white' },
    { outline: false, color: 'purple', className: 'bg-purple-600  text-white' },
    { outline: false, color: 'success', className: 'bg-green-600  text-white' },
    { outline: false, color: 'warning', className: 'bg-yellow-600  text-white' },
  ],
  defaultVariants: {
    color: 'primary',
    size: 'md',
    outline: false,
  },
})

export type ButtonVariants = VariantProps<typeof buttonVariants>

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: string | React.ReactNode
  outline?: true | 'ghost'
  spinner?: boolean
  className?: string
}

export const Button = React.memo((props: Omit<ButtonVariants, keyof ButtonProps> & ButtonProps) => {
  const { children, disabled, className } = props
  const onClick = disabled ? undefined : props.onClick

  const spinnerColorClass = match(props)
    .with({ outline: 'ghost', color: 'dark' }, () => 'fill-gray-700')
    .with({ outline: 'ghost', color: 'light' }, () => 'fill-gray-700')
    .with({ outline: 'ghost', color: 'primary' }, () => 'fill-primary-700')
    .with({ outline: 'ghost', color: 'danger' }, () => 'fill-red-700')
    .with({ outline: 'ghost', color: 'gray' }, () => 'fill-gray-700')
    .with({ outline: 'ghost', color: 'info' }, () => 'fill-blue-700')
    .with({ outline: 'ghost', color: 'purple' }, () => 'fill-purple-700')
    .with({ outline: 'ghost', color: 'success' }, () => 'fill-green-700')
    .with({ outline: 'ghost', color: 'warning' }, () => 'fill-yellow-700')

    .with({ outline: true, color: 'dark' }, () => 'fill-gray-700')
    .with({ outline: true, color: 'light' }, () => 'fill-gray-700')
    .with({ outline: true, color: 'primary' }, () => 'fill-primary-700')
    .with({ outline: true, color: 'danger' }, () => 'fill-red-700')
    .with({ outline: true, color: 'gray' }, () => 'fill-gray-700')
    .with({ outline: true, color: 'info' }, () => 'fill-blue-700')
    .with({ outline: true, color: 'purple' }, () => 'fill-purple-700')
    .with({ outline: true, color: 'success' }, () => 'fill-green-700')
    .with({ outline: true, color: 'warning' }, () => 'fill-yellow-700')
    .with({ color: 'light' }, () => 'fill-gray-800')
    .otherwise(() => 'fill-white')

  let ariaLabel: string | undefined = props['aria-label']
  ariaLabel ??= typeof children === 'string' ? children : undefined

  return (
    <button
      disabled={disabled}
      className={clsx(buttonVariants(props as ButtonVariants), 'whitespace-nowrap', className)}
      onClick={onClick}
      type={props.type}
      aria-label={ariaLabel}
    >
      {props.spinner && <Spinner sizeClassName="w-3 h-3 mr-1 -mt-1" className={spinnerColorClass} />}
      {children}
    </button>
  )
})
